import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft, Plus, X, Save, AlertCircle, Check, Trash2
} from 'lucide-react'
import { getPaddocks, getRanch, reconfigurePaddocks } from '../../services/api'

export default function PaddockReconfigure({ ranchId }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [error, setError] = useState('')
  const [success, setSuccess] = useState(null)
  const [configName, setConfigName] = useState('')
  const [configDescription, setConfigDescription] = useState('')
  const [paddocks, setPaddocks] = useState([])
  const [editingIndex, setEditingIndex] = useState(null)
  const [currentPaddock, setCurrentPaddock] = useState({
    name: '',
    management_type: 'mmg',
    has_kraal: false,
    has_water: true,
    coordinates: [
      { lat: '', lng: '' },
      { lat: '', lng: '' },
      { lat: '', lng: '' },
    ]
  })

  // Fetch current paddocks (for reference)
  const { data: currentPaddocks } = useQuery({
    queryKey: ['paddocks', ranchId],
    queryFn: () => getPaddocks(ranchId),
  })

  // Fetch ranch boundary
  const { data: ranch } = useQuery({
    queryKey: ['ranch', ranchId],
    queryFn: () => getRanch(ranchId),
  })

  const ranchBoundary = ranch?.boundary_coordinates || []

  // Check if any paddock has active grazing
  const hasActiveGrazing = currentPaddocks?.some(
    p => p.status?.current_status === 'grazing'
  )

  // Shoelace formula for area calculation
  const calculatePolygonArea = (coords) => {
    const validCoords = coords.filter(c => c.lat && c.lng)
    if (validCoords.length < 3) return null

    const points = validCoords.map(c => ({
      lat: parseFloat(c.lat),
      lng: parseFloat(c.lng)
    }))

    const n = points.length
    let area = 0
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n
      area += points[i].lng * points[j].lat
      area -= points[j].lng * points[i].lat
    }
    area = Math.abs(area) / 2

    const metersPerDegree = 111320
    const areaM2 = area * metersPerDegree * metersPerDegree
    return areaM2 / 10000
  }

  // Ray casting - point in polygon
  const isPointInPolygon = (point, polygon) => {
    const x = parseFloat(point.lat)
    const y = parseFloat(point.lng)

    let inside = false
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = parseFloat(polygon[i].lat)
      const yi = parseFloat(polygon[i].lng)
      const xj = parseFloat(polygon[j].lat)
      const yj = parseFloat(polygon[j].lng)

      if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
        inside = !inside
      }
    }
    return inside
  }

  // Validate paddock is within ranch boundary
  const validatePaddockInRanch = (paddockCoords) => {
    const validRanchCoords = ranchBoundary.filter(c => c.lat && c.lng)
    if (validRanchCoords.length < 3) return { valid: true, message: '' }

    const validPaddockCoords = paddockCoords.filter(c => c.lat && c.lng)
    for (const point of validPaddockCoords) {
      if (!isPointInPolygon(point, validRanchCoords)) {
        return {
          valid: false,
          message: `Point (${point.lat}, ${point.lng}) is outside ranch boundary`
        }
      }
    }
    return { valid: true, message: '' }
  }

  // Check polygon overlap
  const doPolygonsOverlap = (poly1, poly2) => {
    for (const point of poly1.filter(c => c.lat && c.lng)) {
      if (isPointInPolygon(point, poly2.filter(c => c.lat && c.lng))) return true
    }
    for (const point of poly2.filter(c => c.lat && c.lng)) {
      if (isPointInPolygon(point, poly1.filter(c => c.lat && c.lng))) return true
    }
    return false
  }

  // Add coordinate point
  const addCoordinate = () => {
    setCurrentPaddock(prev => ({
      ...prev,
      coordinates: [...prev.coordinates, { lat: '', lng: '' }]
    }))
  }

  const removeCoordinate = (index) => {
    if (currentPaddock.coordinates.length <= 3) return
    setCurrentPaddock(prev => ({
      ...prev,
      coordinates: prev.coordinates.filter((_, i) => i !== index)
    }))
  }

  const updateCoordinate = (index, field, value) => {
    setCurrentPaddock(prev => ({
      ...prev,
      coordinates: prev.coordinates.map((c, i) =>
        i === index ? { ...c, [field]: value } : c
      )
    }))
  }

  // Add paddock to list
  const addPaddock = () => {
    setError('')
    const validCoords = currentPaddock.coordinates.filter(c => c.lat && c.lng)

    if (!currentPaddock.name.trim()) {
      setError('Paddock name is required')
      return
    }
    if (validCoords.length < 3) {
      setError('At least 3 coordinate points are required')
      return
    }

    // Validate within ranch boundary
    const boundaryCheck = validatePaddockInRanch(currentPaddock.coordinates)
    if (!boundaryCheck.valid) {
      setError(boundaryCheck.message)
      return
    }

    // Check overlap with existing paddocks
    for (let i = 0; i < paddocks.length; i++) {
      if (editingIndex !== null && i === editingIndex) continue
      if (doPolygonsOverlap(validCoords, paddocks[i].coordinates)) {
        setError(`Paddock overlaps with ${paddocks[i].name}`)
        return
      }
    }

    const area = calculatePolygonArea(currentPaddock.coordinates)
    const paddockData = {
      ...currentPaddock,
      coordinates: validCoords,
      area_ha: area?.toFixed(2),
    }

    if (editingIndex !== null) {
      setPaddocks(prev => prev.map((p, i) => i === editingIndex ? paddockData : p))
      setEditingIndex(null)
    } else {
      setPaddocks(prev => [...prev, paddockData])
    }

    // Reset form
    setCurrentPaddock({
      name: '',
      management_type: 'mmg',
      has_kraal: false,
      has_water: true,
      coordinates: [
        { lat: '', lng: '' },
        { lat: '', lng: '' },
        { lat: '', lng: '' },
      ]
    })
  }

  const editPaddock = (index) => {
    const p = paddocks[index]
    setCurrentPaddock({
      name: p.name,
      management_type: p.management_type,
      has_kraal: p.has_kraal,
      has_water: p.has_water,
      coordinates: [...p.coordinates, { lat: '', lng: '' }],
    })
    setEditingIndex(index)
  }

  const removePaddock = (index) => {
    setPaddocks(prev => prev.filter((_, i) => i !== index))
    if (editingIndex === index) {
      setEditingIndex(null)
      setCurrentPaddock({
        name: '',
        management_type: 'mmg',
        has_kraal: false,
        has_water: true,
        coordinates: [{ lat: '', lng: '' }, { lat: '', lng: '' }, { lat: '', lng: '' }]
      })
    }
  }

  // Total area of new paddocks
  const totalNewArea = paddocks.reduce((sum, p) => sum + (parseFloat(p.area_ha) || 0), 0)
  const ranchArea = ranch?.area_ha ? parseFloat(ranch.area_ha) : null

  // Submit reconfiguration
  const reconfigureMutation = useMutation({
    mutationFn: reconfigurePaddocks,
    onSuccess: (data) => {
      queryClient.invalidateQueries(['paddocks'])
      queryClient.invalidateQueries(['paddockSummary'])
      queryClient.invalidateQueries(['paddockConfigurations'])
      setSuccess(data)
    },
    onError: (err) => setError(err.response?.data?.detail || 'Failed to reconfigure paddocks'),
  })

  const handleSubmit = () => {
    setError('')
    if (paddocks.length === 0) {
      setError('At least one paddock is required')
      return
    }

    const data = {
      ranch_id: ranchId,
      name: configName || undefined,
      description: configDescription || undefined,
      paddocks: paddocks.map(p => ({
        name: p.name,
        management_type: p.management_type,
        has_kraal: p.has_kraal,
        has_water: p.has_water,
        boundary_coordinates: p.coordinates,
      })),
    }

    reconfigureMutation.mutate(data)
  }

  // Success view
  if (success) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
          <Check className="h-12 w-12 mx-auto text-green-600 mb-4" />
          <h2 className="text-xl font-bold text-green-900 mb-2">Paddocks Reconfigured</h2>
          <p className="text-green-700 mb-4">
            Configuration v{success.configuration.version} created with {success.paddocks_created} paddocks.
          </p>

          {(success.lantana_reassignment.reassigned > 0 || success.lantana_reassignment.orphaned > 0) && (
            <div className="bg-white rounded-lg p-4 mb-4 text-left">
              <h3 className="font-semibold text-gray-900 mb-2">Lantana Patch Reassignment</h3>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>Reassigned to new paddocks: {success.lantana_reassignment.reassigned}</li>
                <li>Needing manual assignment: {success.lantana_reassignment.orphaned}</li>
                <li>Historical (unchanged): {success.lantana_reassignment.historical}</li>
              </ul>
            </div>
          )}

          <button
            onClick={() => navigate('/paddocks')}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            View Paddocks
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate('/paddocks')} className="text-gray-600 hover:text-gray-900">
          <ArrowLeft className="h-6 w-6" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reconfigure Paddocks</h1>
          <p className="text-gray-600">
            Define a new paddock layout. Current paddocks ({currentPaddocks?.length || 0}) will be archived.
          </p>
        </div>
      </div>

      {/* Active grazing warning */}
      {hasActiveGrazing && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
          <div>
            <p className="font-medium text-red-900">Cannot reconfigure while grazing is active</p>
            <p className="text-sm text-red-700">End all active grazing events before reconfiguring paddocks.</p>
          </div>
        </div>
      )}

      {/* Config name/description */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="font-semibold text-gray-900 mb-4">Configuration Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Configuration Name</label>
            <input
              type="text"
              value={configName}
              onChange={e => setConfigName(e.target.value)}
              placeholder="e.g., Post-Subdivision 2026"
              className="w-full p-2 border rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
            <input
              type="text"
              value={configDescription}
              onChange={e => setConfigDescription(e.target.value)}
              placeholder="Why this reconfiguration?"
              className="w-full p-2 border rounded-lg"
            />
          </div>
        </div>
      </div>

      {/* Added paddocks */}
      {paddocks.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-semibold text-gray-900">New Paddocks ({paddocks.length})</h2>
            <div className="text-sm text-gray-600">
              Total area: {totalNewArea.toFixed(1)} ha
              {ranchArea && (
                <span className={totalNewArea >= ranchArea * 0.9 ? ' text-green-600' : ' text-amber-600'}>
                  {' '}({((totalNewArea / ranchArea) * 100).toFixed(0)}% of ranch)
                </span>
              )}
            </div>
          </div>
          <div className="space-y-2">
            {paddocks.map((p, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <span className="font-medium text-gray-900">P{String(idx + 1).padStart(2, '0')}</span>
                  <span className="ml-2 text-gray-600">{p.name}</span>
                  <span className="ml-2 text-sm text-gray-500">{p.area_ha} ha</span>
                  <span className="ml-2 text-xs text-gray-400">{p.management_type}</span>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => editPaddock(idx)} className="text-blue-600 hover:text-blue-800 text-sm">
                    Edit
                  </button>
                  <button onClick={() => removePaddock(idx)} className="text-red-600 hover:text-red-800">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add paddock form */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="font-semibold text-gray-900 mb-4">
          {editingIndex !== null ? `Edit Paddock: ${paddocks[editingIndex]?.name}` : 'Add New Paddock'}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Paddock Name *</label>
            <input
              type="text"
              value={currentPaddock.name}
              onChange={e => setCurrentPaddock(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., North-A"
              className="w-full p-2 border rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Management Type</label>
            <select
              value={currentPaddock.management_type}
              onChange={e => setCurrentPaddock(prev => ({ ...prev, management_type: e.target.value }))}
              className="w-full p-2 border rounded-lg"
            >
              <option value="mmg">MMG (Multi-paddock Managed Grazing)</option>
              <option value="rotational">Rotational</option>
              <option value="continuous">Continuous</option>
              <option value="strip">Strip Grazing</option>
              <option value="holding">Holding</option>
            </select>
          </div>
        </div>

        <div className="flex gap-4 mb-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={currentPaddock.has_water}
              onChange={e => setCurrentPaddock(prev => ({ ...prev, has_water: e.target.checked }))}
              className="rounded"
            />
            <span className="text-sm text-gray-700">Has Water</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={currentPaddock.has_kraal}
              onChange={e => setCurrentPaddock(prev => ({ ...prev, has_kraal: e.target.checked }))}
              className="rounded"
            />
            <span className="text-sm text-gray-700">Has Kraal</span>
          </label>
        </div>

        {/* Coordinates */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Boundary Coordinates (min 3 points)
          </label>
          <div className="space-y-2">
            {currentPaddock.coordinates.map((coord, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <span className="text-xs text-gray-400 w-6">{idx + 1}.</span>
                <input
                  type="number"
                  step="any"
                  value={coord.lat}
                  onChange={e => updateCoordinate(idx, 'lat', e.target.value)}
                  placeholder="Latitude"
                  className="flex-1 p-2 border rounded-lg text-sm"
                />
                <input
                  type="number"
                  step="any"
                  value={coord.lng}
                  onChange={e => updateCoordinate(idx, 'lng', e.target.value)}
                  placeholder="Longitude"
                  className="flex-1 p-2 border rounded-lg text-sm"
                />
                {currentPaddock.coordinates.length > 3 && (
                  <button onClick={() => removeCoordinate(idx)} className="text-red-500 hover:text-red-700">
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
          <button onClick={addCoordinate} className="mt-2 text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1">
            <Plus className="h-4 w-4" /> Add Point
          </button>

          {/* Area preview */}
          {(() => {
            const area = calculatePolygonArea(currentPaddock.coordinates)
            return area ? (
              <p className="mt-2 text-sm text-gray-600">
                Calculated area: <span className="font-medium">{area.toFixed(2)} ha</span>
              </p>
            ) : null
          })()}
        </div>

        <button
          onClick={addPaddock}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          {editingIndex !== null ? <Check className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
          {editingIndex !== null ? 'Update Paddock' : 'Add Paddock'}
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Submit */}
      <div className="flex justify-end gap-4">
        <button
          onClick={() => navigate('/paddocks')}
          className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={paddocks.length === 0 || hasActiveGrazing || reconfigureMutation.isPending}
          className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save className="h-5 w-5" />
          {reconfigureMutation.isPending ? 'Reconfiguring...' : `Reconfigure (${paddocks.length} paddocks)`}
        </button>
      </div>
    </div>
  )
}
